import { z } from "zod";
import { IsoDateTimeSchema, StableIdSchema } from "./common.js";

export const ConfigurationRevisionSchema = z.string().regex(/^cfgrev_[a-f0-9]{24}$/);

export const RawConfigurationSchema = z
  .object({
    filename: z.literal("config.yaml"),
    yaml: z.string().max(256 * 1024),
    revision: ConfigurationRevisionSchema,
    updatedAt: IsoDateTimeSchema,
  })
  .strict();

export const SaveRawConfigurationRequestSchema = z
  .object({
    yaml: z.string().max(256 * 1024),
    revision: ConfigurationRevisionSchema,
    confirm: z.literal("replace_config_yaml"),
  })
  .strict();

export const EnvironmentVariableNameSchema = z.string().regex(/^[A-Z][A-Z0-9_]{0,127}$/);

export const EnvironmentVariableSchema = z
  .object({
    key: EnvironmentVariableNameSchema,
    isSet: z.boolean(),
    description: z.string().max(500),
    category: z.string().max(80),
    isPassword: z.boolean(),
    advanced: z.boolean(),
    custom: z.boolean(),
    provider: z.string().max(80).nullable(),
    providerLabel: z.string().max(120).nullable(),
  })
  .strict();

export const EnvironmentCatalogSchema = z
  .object({
    variables: z.array(EnvironmentVariableSchema).max(500),
    updatedAt: IsoDateTimeSchema,
  })
  .strict();

export const UpdateEnvironmentVariableRequestSchema = z.discriminatedUnion("action", [
  z
    .object({
      action: z.literal("replace"),
      value: z
        .string()
        .min(1)
        .max(16 * 1024),
      confirm: z.literal("replace_environment_variable"),
    })
    .strict(),
  z
    .object({
      action: z.literal("remove"),
      confirm: z.literal("remove_environment_variable"),
    })
    .strict(),
]);

export const EnvironmentMutationResponseSchema = z
  .object({
    ok: z.literal(true),
    key: EnvironmentVariableNameSchema,
    isSet: z.boolean(),
  })
  .strict();

export const ReauthenticateRequestSchema = z
  .object({ password: z.string().min(1).max(1024) })
  .strict();

export const ReauthenticateResponseSchema = z
  .object({
    token: z.string().min(32).max(256),
    expiresAt: IsoDateTimeSchema,
  })
  .strict();

export const GatewayRestartRequestSchema = z
  .object({
    commandId: StableIdSchema,
    confirm: z.literal("restart_gateway"),
  })
  .strict();

export const GatewayRestartResponseSchema = z
  .object({
    commandId: StableIdSchema,
    state: z.enum(["accepted", "unknown"]),
    message: z.string().min(1).max(500),
    requestedAt: IsoDateTimeSchema,
  })
  .strict();

export type RawConfiguration = z.infer<typeof RawConfigurationSchema>;
export type SaveRawConfigurationRequest = z.infer<typeof SaveRawConfigurationRequestSchema>;
export type EnvironmentVariable = z.infer<typeof EnvironmentVariableSchema>;
export type EnvironmentCatalog = z.infer<typeof EnvironmentCatalogSchema>;
export type UpdateEnvironmentVariableRequest = z.infer<
  typeof UpdateEnvironmentVariableRequestSchema
>;
export type EnvironmentMutationResponse = z.infer<typeof EnvironmentMutationResponseSchema>;
export type ReauthenticateRequest = z.infer<typeof ReauthenticateRequestSchema>;
export type ReauthenticateResponse = z.infer<typeof ReauthenticateResponseSchema>;
export type GatewayRestartRequest = z.infer<typeof GatewayRestartRequestSchema>;
export type GatewayRestartResponse = z.infer<typeof GatewayRestartResponseSchema>;
